// src/redux/threadsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  orderBy,
  Unsubscribe,
  DocumentData,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import type { RootState } from "./store";
import { increment } from "firebase/firestore";


// --- Types ---
export interface Message {
  id?: string;
  senderId: string;
  text: string;
  type: string;
  timestamp: any;
   senderProfilePicture?: string;
     senderName?: string;

  status: Record<string, string>;
}

export interface Thread {
  id: string;
  participants: string[];
  lastMessage?: Message;
  unread: Record<string, number>;
  updatedAt: any;
}

interface ThreadsState {
  threads: Thread[];
  messages: Record<string, Message[]>;
  loading: boolean;
  error: string | null;
}

const initialState: ThreadsState = {
  threads: [],
  messages: {},
  loading: false,
  error: null,
};

// --- Helpers ---
const getThreadId = (uid1: string, uid2: string): string => {
  return [uid1, uid2].sort().join("_");
};

const db = getFirestore();
const auth = getAuth();

// --- Thunks ---
export const createOrGetThread = createAsyncThunk<
  Thread,
  { userIdB: string }
>("threads/createOrGet", async ({ userIdB }, { rejectWithValue }) => {
  try {
const userIdA = auth.currentUser?.uid; // ✅ only uid
    if (!userIdA) throw new Error("User not authenticated");

    const threadId = getThreadId(userIdA, userIdB);
    const threadRef = doc(db, "threads", threadId);
    const snapshot = await getDoc(threadRef);

    if (snapshot.exists()) {
      const data = snapshot.data() || {};
      return {
        id: threadId,
        ...data,
        updatedAt: data.updatedAt?.toMillis?.() || null,
      } as Thread;
    }

    await setDoc(threadRef, {
      id: threadId,
      participants: [userIdA, userIdB],
      lastMessage: null,
      unread: { [userIdA]: 0, [userIdB]: 0 },
      updatedAt: serverTimestamp(),
    });

    const createdSnap = await getDoc(threadRef);
    const createdData = createdSnap.data() || {};

    return {
      id: createdSnap.id,
      ...createdData,
      updatedAt: createdData.updatedAt?.toMillis?.() || null,
    } as Thread;
  } catch (err: any) {
    console.error("🔥 createOrGetThread error:", err);
    return rejectWithValue(err.message);
  }
});

export const sendMessage = createAsyncThunk<
  void,
  { threadId: string; text: string },
  { state: RootState }
>(
  "threads/sendMessage",
  async ({ threadId, text }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const sender = state.auth.user;

  if (!sender?.userId) throw new Error("User not authenticated");

const threadRef = doc(db, "threads", threadId);
const messagesRef = collection(threadRef, "messages");

const message = {
  senderId: sender.userId, // ✅ fix
  senderName: sender.firstName || sender.lastName || "User",
  senderProfilePicture: sender.profilePicture || null,
  text,
  type: "text",
  status: {},
  timestamp: serverTimestamp(),
};


      const msgDoc = await addDoc(messagesRef, message);
      const saved = await getDoc(msgDoc);
      const savedData = saved.data();

// get receiverId from threadId
const receiverId = threadId
  .split("_")
  .find((id) => id !== sender.userId);

await updateDoc(threadRef, {
  lastMessage: {
    id: msgDoc.id,
    ...savedData,
  },
  updatedAt: serverTimestamp(),

  // sender unread reset
  [`unread.${sender.userId}`]: 0,

  // receiver unread increment
  [`unread.${receiverId}`]: increment(1),
});



     // console.log("[sendMessage] Saved message with id:", msgDoc.id);
    } catch (err: any) {
      console.error("🔥 sendMessage error:", err);
      return rejectWithValue(err.message);
    }
  }
);

export const upsertLastMessage = createAsyncThunk<
  void,
  { threadId: string; message: Message }
>(
  "threads/upsertLastMessage",
  async ({ threadId, message }, { rejectWithValue }) => {
    try {
      const threadRef = doc(db, "threads", threadId);

      const lastMessage = {
        id: message.id || "",
        senderId: message.senderId,
        text: message.text,
        type: message.type,
        timestamp: message.timestamp || serverTimestamp(),
      };

      await setDoc(
        threadRef,
        {
          lastMessage,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      console.log("[upsertLastMessage] Updated thread:", threadId, lastMessage);
    } catch (err: any) {
      //console.error("🔥 upsertLastMessage error:", err);
      return rejectWithValue(err.message);
    }
  }
);

// --- Listeners with unsubscribe ---
export const listenToMessages = createAsyncThunk<
  Unsubscribe | void,
  { threadId: string }
>("threads/listenToMessages", async ({ threadId }, { dispatch, rejectWithValue }) => {
  try {
    const messagesRef = collection(db, "threads", threadId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs: Message[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();

        // ✅ Explicitly build the Message
        const msg: Message = {
          id: docSnap.id,
          senderId: data.senderId || "", // required
          text: data.text || "",
          type: data.type || "text",
          status: data.status || {},
          timestamp: data.timestamp?.toMillis?.() || null,
        };

        msgs.push(msg);
      });

      dispatch(setMessages({ threadId, messages: msgs }));
    });

    return unsubscribe;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});


export const listenToLastMessage = createAsyncThunk<
  Unsubscribe | void,
  { threadId: string }
>("threads/listenToLastMessage", async ({ threadId }, { dispatch, rejectWithValue }) => {
  try {
    const threadRef = doc(db, "threads", threadId);

    const unsubscribe = onSnapshot(threadRef, (docSnap) => {
      if (!docSnap.exists()) return;

      const data = docSnap.data();
      if (!data?.lastMessage) return;

      const lastMessage = {
        ...data.lastMessage,
        timestamp: data.lastMessage?.timestamp?.toMillis?.() || null,
      };

      dispatch(updateThreadLastMessage({ threadId, lastMessage }));
    });

    return unsubscribe;
  } catch (err: any) {
    console.error("🔥 listenToLastMessage error:", err);
    return rejectWithValue(err.message);
  }
});

export const listenToUserConversations = createAsyncThunk<
  Unsubscribe | void,
  void
>("threads/listenToUserConversations", async (_, { dispatch, rejectWithValue }) => {
  try {
const uid = auth.currentUser?.uid; // ✅
    if (!uid) throw new Error("User not authenticated");

    const q = query(
      collection(db, "threads"),
      where("participants", "array-contains", uid),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const threads: Thread[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();

        // ✅ Build the Thread object explicitly
        const thread: Thread = {
          id: docSnap.id,
          participants: data.participants || [], // required
          unread: data.unread || {},             // required
          updatedAt: data.updatedAt?.toMillis?.() || null,
          lastMessage: data.lastMessage
            ? {
                ...data.lastMessage,
                timestamp: data.lastMessage?.timestamp?.toMillis?.() || null,
              }
            : undefined,
        };

        threads.push(thread);
      });

      dispatch(setThreads(threads));
    });

    return unsubscribe;
  } catch (err: any) {
    console.error("🔥 listenToUserConversations error:", err);
    return rejectWithValue(err.message);
  }
});

// --- Slice ---
const threadsSlice = createSlice({
  name: "threads",
  initialState,
  reducers: {
    setThreads: (state, action: PayloadAction<Thread[]>) => {
      state.threads = action.payload;
    },
    setMessages: (
      state,
      action: PayloadAction<{ threadId: string; messages: Message[] }>
    ) => {
      state.messages[action.payload.threadId] = action.payload.messages;
    },
    updateThreadLastMessage: (
      state,
      action: PayloadAction<{ threadId: string; lastMessage: any }>
    ) => {
      const { threadId, lastMessage } = action.payload;
      const thread = state.threads.find((t) => t.id === threadId);
      if (thread) {
        thread.lastMessage = lastMessage;
        thread.updatedAt = Date.now();
      }
    },
  },
});

export const { setThreads, setMessages, updateThreadLastMessage } =
  threadsSlice.actions;
export default threadsSlice.reducer;
